import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  appointCommander,
  endSession,
  openVote,
  rconSay,
  rconSwitch,
  sendCommand,
  withdrawCommand,
  type SessionEvent,
} from '../api/missions';
import { fetchSessionAftermath, type MatchDetail } from '../api/reports';
import { ca } from '../i18n/ca';
import { AccountPicker } from './AccountPicker';
import { JoinCard, ObjectivesCard, VotesPanel } from './DecisionPanels';
import { EnemyReinforcementCard, EnvironmentCard } from './DirectionOrders';
import { worldSizeOf } from './designModel';
import { MatchTables } from './MatchArmament';
import { MissionMap } from './MissionMap';
import { useObjectiveEditor } from './objectiveEditing';
import { PlannedChip } from './Planned';
import { plannedEntry, type PlannedId } from './plannedRegistry';
import { ScriptPhases } from './ScriptPhases';
import { playersOnServer } from './sessionState';
import { useConfirm } from './useConfirm';
import { Shell } from './Shell';
import {
  describeEvent,
  errorText,
  clockOf,
  objectiveStatesOf,
  useSessionLive,
  withAdded,
} from './useSessionLive';

const SUPPORTS = ['artillery', 'cas', 'transport', 'supplyDrop', 'uav', 'medevac'];

/** The runtime's fifteen primitives (addon/addons/main/functions/fn_prim_*.sqf), named once in ca-session. */
const OBJECTIVE_TYPES = Object.keys(ca.objectiveUi.types);

/** Under 15 fps the runtime shrinks the AI activation radius; above 25 it grows back (fn_healthLoop.sqf). */
function fpsClass(fps: number): string {
  if (fps < 15) return 'red';
  if (fps < 25) return 'amber';
  return '';
}

/**
 * The direction page (UI doc 5.8), in three fixed zones (the owner, 2026-10-12): a status
 * strip that stays in view (state, minute, players, FPS, groups, the runtime's self-check
 * verdict and how stale the page is), the map as large as the room allows on the left with
 * the night's timeline right under it, and on the right the action panel, headed by its
 * name: the objectives, what can be done right now and how to get in. The night's phases
 * and, collapsed, the technical details and what is designed and not built yet follow the
 * timeline. Once the night is over the map carries the session report's layers (where the
 * players went, where people fell) and the report's tables follow the timeline (the owner,
 * web card 197, 2026-10-18), and the page keeps only what makes sense afterwards: the map with
 * those layers, the timeline, the report and how each objective ended. Every live control
 * (actions, reveals, edits, votes, commands, the radio, the commander) is gone, not greyed out
 * (the owner, web card 222, 2026-10-19: "hi ha blocs que no tenen sentit en una missió ja
 * acabada").
 * The page never talks to the game directly, and ending the mission is the one action that
 * asks twice.
 */
export function DirectionPage() {
  const { code = '' } = useParams();
  const {
    data,
    events,
    votes,
    error,
    setData,
    setError,
    setVotes,
    commands,
    setCommands,
    refresh,
    lastReceivedAt,
  } = useSessionLive(code, 'direction');
  const [notice, setNotice] = useState<string | null>(null);
  const [radioFrom, setRadioFrom] = useState('COMANDAMENT');
  const [radioText, setRadioText] = useState('');
  const [chat, setChat] = useState('');
  const [commanderHandle, setCommanderHandle] = useState('');
  /** The handles a commander vote will offer, added one by one from the field's suggestions. */
  const [commanderCandidates, setCommanderCandidates] = useState<string[]>([]);
  const [candidateTyped, setCandidateTyped] = useState('');
  const [newType, setNewType] = useState('clear_area');
  const [newTitle, setNewTitle] = useState('');
  const [newPriority, setNewPriority] = useState<'main' | 'secondary' | 'hidden'>('secondary');
  const [newRadius, setNewRadius] = useState('250');
  const [newPos, setNewPos] = useState<[number, number] | null>(null);
  const [reinforceTo, setReinforceTo] = useState('');
  const [eventKind, setEventKind] = useState('enemy_patrol');
  const [eventObjective, setEventObjective] = useState('');
  const [weather, setWeather] = useState('clear');
  const [outcome, setOutcome] = useState('success');
  const [endOpen, setEndOpen] = useState(false);
  const [endConfirm, setEndConfirm] = useState('');
  const [selected, setSelected] = useState<string | null>(null);
  const [confirm, confirmDialog] = useConfirm();
  const editor = useObjectiveEditor(data?.missionCode ?? '', !!data?.canDirect, setError, refresh);
  /** The session report's facts, once the night is over: what the panel counted of it. */
  const [aftermath, setAftermath] = useState<MatchDetail | null>(null);
  const [showMovement, setShowMovement] = useState(true);
  const [showDeaths, setShowDeaths] = useState(true);
  const state = data?.session.state;
  useEffect(() => {
    if (!code || state !== 'ENDED') return;
    let current = true;
    fetchSessionAftermath(code)
      .then((d) => current && setAftermath(d))
      .catch(() => current && setAftermath(null));
    return () => {
      current = false;
    };
  }, [code, state]);

  if (!data) {
    return (
      <Shell title={ca.direction} width="wide">
        {error && (
          <p className="error" role="alert">
            {error}
          </p>
        )}
      </Shell>
    );
  }
  const def = withAdded(data.definition, events);
  const live = data.session.state === 'LIVE';
  const objectiveStates = objectiveStatesOf(events);
  const lastHealth = [...events].reverse().find((e) => e.kind === 'health');
  const server = lastHealth?.payload.server ?? {};
  const serverFps = typeof server.fps === 'number' ? server.fps : null;
  const activeGroups = typeof server.activeGroups === 'number' ? server.activeGroups : null;
  const hcFps = typeof server.hcFps === 'number' ? server.hcFps : null;
  const schedulerLag = typeof server.schedulerLag === 'number' ? server.schedulerLag : null;
  const acks = events
    .filter((e) => e.kind === 'commandAck')
    .slice(-8)
    .reverse();
  // A scripted night's own bus has its card (the phase backbone): the timeline keeps the rest.
  const scriptEvents = events.filter((e) => e.kind === 'script').length;
  const timeline = events
    .filter(
      (e) =>
        e.kind !== 'health' &&
        e.kind !== 'distance' &&
        e.kind !== 'commandAck' &&
        e.kind !== 'script',
    )
    .slice(-30)
    .reverse();
  const clock = clockOf(events);
  // A failed main objective no longer ends the night by itself: the decision is
  // taken here, with the failure in front of whoever is directing it.
  const failures = def.objectives.filter(
    (o) => (o.priority ?? 'main') === 'main' && objectiveStates.get(o.id)?.state === 'failed',
  );
  const built = [...events].reverse().find((e) => e.kind === 'built');
  // A client on another island than the server's: the server's own world is a
  // fact, the client's a claim it filed itself, and the last word per player wins.
  const worldMismatches = Array.from(
    events
      .filter((e) => e.kind === 'log' && e.payload.server?.clientReport === 'world')
      .reduce((by, e) => {
        const uid = String(e.payload.client?.uid ?? e.payload.server?.player ?? '');
        by.set(uid, e);
        return by;
      }, new Map<string, SessionEvent>())
      .values(),
  ).filter((e) => e.payload.server?.mismatch === true);
  // A client that read no mission.sqm at all: the number behind four nights on the wrong island.
  const unreadMissions = events.filter(
    (e) =>
      e.kind === 'log' &&
      e.payload.server?.clientReport === 'world' &&
      e.payload.server?.missionRead === false,
  );
  // What the runtime found wrong with what it built, at the start.
  const selfCheck = [...events].reverse().find((e) => e.kind === 'selfCheck');
  const selfCheckIssues = Array.isArray(selfCheck?.payload.server?.issues)
    ? (selfCheck!.payload.server!.issues as {
        kind: string;
        id: string;
        position: number[];
        height: number;
      }[])
    : [];

  const t = ca.directionPage;
  const issueCount = selfCheckIssues.length;
  const players = playersOnServer(events);
  const aiUnits = typeof server.aiUnits === 'number' ? server.aiUnits : null;
  // The mission clock of the last thing the runtime said; before the first event, nothing.
  const missionMinute = events.length
    ? Math.floor(Math.max(...events.map((e) => e.at)) / 60)
    : null;
  const ended = data.session.state === 'ENDED';

  /**
   * What the design asks for and the code does not do yet, gathered in one place instead of
   * scattered through the page (the owner, 2026-10-12). Each one keeps its «Pendent» chip.
   */
  const upcoming: { key: string; label: string; plannedId: PlannedId }[] = [
    { key: 'aiDensity', label: t.aiDensity, plannedId: 'direction-ai-density' },
    { key: 'playerPositions', label: t.playerPositions, plannedId: 'direction-positions' },
    {
      key: 'supportsUsedBy',
      label: `${ca.supportsAdd}: ${t.supportsUsedBy}`,
      plannedId: 'direction-support-usage',
    },
    { key: 'assign', label: t.assignExtend, plannedId: 'objective-assign-extend' },
    { key: 'schedule', label: t.scheduleMessage, plannedId: 'direction-schedule-message' },
    { key: 'undo', label: `${ca.commandQueue}: ${t.undoWindow}`, plannedId: 'direction-undo' },
    ...(hcFps === null
      ? [{ key: 'hcFps', label: t.hcFps, plannedId: 'direction-hc-fps' as const }]
      : []),
    ...(schedulerLag === null
      ? [
          {
            key: 'schedulerLag',
            label: t.schedulerLag,
            plannedId: 'direction-scheduler-lag' as const,
          },
        ]
      : []),
  ];

  const command = (kind: string, payload: Record<string, unknown>) =>
    sendCommand(code, kind, payload)
      .then(() => {
        setNotice(`${ca.commandSent}: ${kind}`);
        setError(null);
      })
      .catch((e) => setError(errorText(e)));

  const say = (e: React.FormEvent) => {
    e.preventDefault();
    rconSay(code, chat)
      .then(() => {
        setChat('');
        setNotice(ca.commandSent);
        setError(null);
      })
      .catch((err) => setError(errorText(err)));
  };

  async function cancelObjective(o: { id: string; title?: string | null }) {
    const ask = ca.directionPage.cancelObjectiveConfirm(o.title ?? o.id);
    if (
      !(await confirm({ ...ask, confirmLabel: ca.directionPage.cancelObjectiveDo, danger: true }))
    )
      return;
    await command('cancelObjective', { id: o.id });
  }

  const appoint = (handle: string) =>
    appointCommander(code, handle, 'direction')
      .then((d) => {
        setData(d);
        setCommanderHandle('');
        setError(null);
      })
      .catch((err) => setError(errorText(err)));

  const addCandidate = (handle: string) => {
    const clean = handle.trim().replace(/^@/, '');
    if (!clean) return;
    setCommanderCandidates((list) =>
      list.some((h) => h.toLowerCase() === clean.toLowerCase()) ? list : [...list, clean],
    );
    setCandidateTyped('');
  };

  /**
   * The commander of the night, first in the group of what is done now, with its two ways
   * side by side: the director names one, or the players vote one (the owner, web card 223,
   * 2026-10-19: «Que el grup el voti» sat collapsed at the foot of the panel, and nobody
   * found it). Both fields suggest the accounts that match what is typed.
   */
  const commanderCard = (
    <article className="card commander-card" aria-label={ca.live.commanderTitle}>
      <h2>
        <i className="bi bi-star" aria-hidden="true" /> {ca.live.commanderTitle}
      </h2>
      <p>
        {data.commander
          ? ca.live.commander(`${data.commander.displayName} (@${data.commander.handle})`)
          : ca.live.commanderNone}
      </p>
      <p className="muted small">{ca.live.commanderHint}</p>
      <div className="commander-ways">
        <section aria-label={ca.live.commanderAppointTitle}>
          <h3>{ca.live.commanderAppointTitle}</h3>
          <form
            className="row"
            aria-label={ca.live.commanderAppointTitle}
            onSubmit={(e) => {
              e.preventDefault();
              if (commanderHandle.trim()) void appoint(commanderHandle.trim());
            }}
          >
            <AccountPicker
              code={code}
              label={ca.live.commanderHandle}
              value={commanderHandle}
              onChange={setCommanderHandle}
            />
            <button type="submit" className="primary" disabled={!commanderHandle.trim()}>
              <i className="bi bi-person-check" aria-hidden="true" /> {ca.live.commanderAppoint}
            </button>
          </form>
          {data.commander && (
            <button type="button" className="link" onClick={() => void appoint('')}>
              {ca.live.commanderClear}
            </button>
          )}
        </section>
        {/* The vote names the winner by handle when it closes (CommanderService). */}
        <section aria-label={ca.live.commanderVote}>
          <h3>{ca.live.commanderVote}</h3>
          <p className="muted small">{ca.live.commanderVoteHint}</p>
          <form
            className="form"
            aria-label={ca.live.commanderVote}
            onSubmit={(e) => {
              e.preventDefault();
              if (commanderCandidates.length < 2) return;
              void openVote(code, {
                key: 'commander',
                question: ca.live.commanderVoteQuestion,
                options: commanderCandidates,
                closesAt: null,
              })
                .then((v) => {
                  setVotes(v);
                  setCommanderCandidates([]);
                  setNotice(ca.live.commanderVoteOpened);
                  setError(null);
                })
                .catch((err) => setError(errorText(err)));
            }}
          >
            {commanderCandidates.length === 0 ? (
              <p className="muted small">{ca.live.commanderVoteNone}</p>
            ) : (
              <ul className="plain row candidate-chips" aria-label={ca.live.commanderVote}>
                {commanderCandidates.map((h) => (
                  <li key={h} className="chip">
                    @{h}
                    <button
                      type="button"
                      className="ghost small"
                      aria-label={ca.live.commanderVoteRemove(h)}
                      onClick={() => setCommanderCandidates((list) => list.filter((x) => x !== h))}
                    >
                      <i className="bi bi-x" aria-hidden="true" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <div className="row">
              <AccountPicker
                code={code}
                label={ca.live.commanderVoteCandidates}
                value={candidateTyped}
                onChange={setCandidateTyped}
                onPick={(c) => addCandidate(c.handle)}
                exclude={commanderCandidates}
              />
              <button
                type="button"
                disabled={!candidateTyped.trim()}
                onClick={() => addCandidate(candidateTyped)}
              >
                <i className="bi bi-plus" aria-hidden="true" /> {ca.live.commanderVoteAdd}
              </button>
            </div>
            <button type="submit" disabled={commanderCandidates.length < 2}>
              <i className="bi bi-check2-square" aria-hidden="true" /> {ca.live.commanderVoteOpen}
            </button>
          </form>
        </section>
      </div>
    </article>
  );

  return (
    <Shell
      title={ca.direction}
      kicker={<Link to={`/s/${code}`}>{data.missionTitle}</Link>}
      actions={
        live &&
        !endOpen && (
          <button type="button" className="danger" onClick={() => setEndOpen(true)}>
            {ca.endMission}
          </button>
        )
      }
      width="wide"
    >
      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}
      {confirmDialog}
      {notice && !error && (
        <p className="hint" role="status">
          {notice}
        </p>
      )}
      {/* Every other command is one click; ending the mission is the one whose blast radius
          gets a typed confirmation (UI doc 5.8 principle 7). */}
      {endOpen && (
        <div className="card danger-zone">
          <label>
            {ca.directionPage.endMissionConfirm(code)}
            <input value={endConfirm} onChange={(e) => setEndConfirm(e.target.value)} />
          </label>
          <label>
            {ca.outcome}
            <select value={outcome} onChange={(e) => setOutcome(e.target.value)}>
              {ca.endOutcomes.map((k) => (
                <option key={k} value={k}>
                  {ca.outcomes[k]}
                </option>
              ))}
            </select>
          </label>
          <div className="row actions">
            <button type="button" onClick={() => setEndOpen(false)}>
              {ca.cancel}
            </button>
            <span className="grow" />
            <button
              type="button"
              className="danger"
              disabled={endConfirm !== code}
              onClick={() =>
                void command('endMission', { outcome }).then(() =>
                  endSession(code, outcome, 'direction').then((d) => {
                    setData(d);
                    setEndOpen(false);
                  }),
                )
              }
            >
              {ca.endMission}
            </button>
          </div>
        </div>
      )}

      {/* Zone 1: the status strip, always in view under the page header. */}
      <section className="status-strip" aria-label={t.statusStrip}>
        <div className="strip-state row">
          {live ? (
            <span className="chip live">
              <span className="dot live" aria-hidden="true" />
              {ca.sessionStates.LIVE}
            </span>
          ) : (
            <span className="chip">{ca.sessionStates[data.session.state]}</span>
          )}
          {ended && data.session.outcome && (
            <span className="chip">
              {ca.outcomes[data.session.outcome] ?? data.session.outcome}
            </span>
          )}
        </div>
        <dl className="strip-stats">
          {live && (
            <div className="stat">
              <dt>{t.stripMinute}</dt>
              <dd className="num">{missionMinute !== null ? `${missionMinute}′` : '—'}</dd>
            </div>
          )}
          <div className="stat">
            <dt>{t.stripPlayers}</dt>
            <dd className="num">{players !== null ? players : '—'}</dd>
          </div>
          <div className="stat">
            <dt>{t.serverFps}</dt>
            <dd className={`num ${serverFps !== null ? fpsClass(serverFps) : ''}`}>
              {serverFps !== null ? serverFps.toFixed(0) : '—'}
            </dd>
          </div>
          <div className="stat">
            <dt>{t.activeGroups}</dt>
            <dd className="num">{activeGroups !== null ? activeGroups : '—'}</dd>
          </div>
          {aiUnits !== null && (
            <div className="stat">
              <dt>{t.stripAiUnits}</dt>
              <dd className="num">{aiUnits}</dd>
            </div>
          )}
          {hcFps !== null && (
            <div className="stat">
              <dt>{t.hcFps}</dt>
              <dd className="num">{hcFps.toFixed(0)}</dd>
            </div>
          )}
          {schedulerLag !== null && (
            <div className="stat">
              <dt>{t.schedulerLag}</dt>
              <dd className="num">{schedulerLag}</dd>
            </div>
          )}
        </dl>
        <div className="strip-verdict row">
          {selfCheck ? (
            issueCount === 0 ? (
              <span
                className="chip ok"
                title={t.selfCheckClean(Number(selfCheck.payload.server?.checked ?? 0))}
              >
                <i className="bi bi-check-circle" aria-hidden="true" />
                {t.noProblems}
              </span>
            ) : (
              <span className="chip warn">
                <i className="bi bi-exclamation-triangle" aria-hidden="true" />
                {t.selfCheckCount(issueCount)}
              </span>
            )
          ) : (
            <span className="chip">{t.selfCheckNone}</span>
          )}
          <Updated since={lastReceivedAt} />
        </div>
      </section>

      {worldMismatches.map((e) => (
        <p
          key={String(e.payload.client?.uid ?? e.payload.server?.player)}
          className="warn"
          role="alert"
        >
          {t.worldMismatch(
            String(e.payload.server?.player ?? e.payload.client?.player ?? '?'),
            String(e.payload.client?.worldName ?? '?'),
            String(e.payload.server?.world ?? '?'),
          )}
        </p>
      ))}
      {unreadMissions.map((e) => (
        <p
          key={'unread-' + String(e.payload.client?.uid ?? e.payload.server?.player)}
          className="warn"
          role="alert"
        >
          {t.missionUnread(String(e.payload.server?.player ?? e.payload.client?.player ?? '?'))}
        </p>
      ))}
      {issueCount > 0 && (
        <details className="self-check warn" open>
          <summary>
            <strong>{t.selfCheck}</strong> {t.selfCheckCount(issueCount)}
          </summary>
          <ul>
            {selfCheckIssues.slice(0, 12).map((i, n) => (
              <li key={n}>{t.selfCheckIssue(i.kind, i.id, i.position, i.height)}</li>
            ))}
          </ul>
        </details>
      )}
      {ended ? (
        <p className="notice">{t.ended}</p>
      ) : (
        !live && <p className="notice warn">{t.notLive}</p>
      )}
      {clock && (
        <p className="notice">
          {t.clock(Math.max(0, Math.round(clock.remaining / 60)), clock.phase)}
        </p>
      )}

      <div className="direction-grid">
        {/* Zone 2: the map, as large as the room allows (and first on a phone). */}
        <div className="dir-map">
          <article className="card map-card" aria-label={ended ? t.mapEnded : t.map}>
            {ended && (
              <MapLayers
                hasReport={!!aftermath}
                movement={showMovement}
                deaths={showDeaths}
                onMovement={setShowMovement}
                onDeaths={setShowDeaths}
              />
            )}
            {/* Whoever directs sees everything, the enemy included: it is their job to know. */}
            <MissionMap
              world={def.terrain.worldName}
              worldSize={worldSizeOf(def)}
              objectives={def.objectives.filter(
                (o) => o.priority !== 'hidden' || objectiveStates.has(o.id),
              )}
              start={def.sides.players.startPosition}
              area={def.area}
              markers={def.markers}
              forces={def.forces}
              intel={def.intel}
              civilians={def.civilians?.zones}
              heat={showDeaths ? aftermath?.deathHeat : undefined}
              movement={showMovement ? aftermath?.movementHeat : undefined}
              height={1600}
              wide
              fit="area"
              selected={selected}
              onSelect={(sel) =>
                setSelected((s) => (sel.kind === 'objective' && s === sel.id ? null : sel.id))
              }
              onPlace={ended ? undefined : (x, y) => setNewPos([x, y])}
            />
          </article>
          {/* Under the map, in as many columns as fit: what is read, not acted on. The
              panel on the right keeps what is done (the owner, 2026-10-12: no long
              column beside an empty space under the map). */}
          {/* Right under the map, the whole width of its column: what has happened
              (the owner, web card 197, 2026-10-18: it had flowed beside the phases). */}
          <section className="card dir-timeline" aria-label={ca.timeline}>
            <h2>{ca.timeline}</h2>
            {timeline.length === 0 && <p className="muted small">{ca.noEventsYet}</p>}
            <ul className="activity">
              {timeline.map((e) => (
                <li key={e.seq}>
                  <span className="muted small num">{Math.floor(e.at / 60)}′</span>{' '}
                  <code>{ca.eventKinds[e.kind] ?? e.kind}</code> {describeEvent(e)}
                </li>
              ))}
            </ul>
          </section>
          {/* Once the night is over, what the report says of it comes right after what
              happened: the objectives' outcome first (web card 222, 2026-10-19). */}
          {aftermath && (
            <section className="card dir-report" aria-label={ca.reportsPage2.detail}>
              <h2>{ca.reportsPage2.detail}</h2>
              <MatchTables detail={aftermath} />
            </section>
          )}
          <div className="dir-under">
            <ScriptPhases code={code} trigger={scriptEvents} live={live} />
            <details className="card technical">
              <summary>{t.technical}</summary>
              <p className="muted small">{ca.briefingReads(data.briefingReads ?? 0)}</p>
              {built && (
                <p className="muted small">
                  {t.built(
                    Array.isArray((built.payload.server as Record<string, unknown>)?.objectives)
                      ? ((built.payload.server as Record<string, unknown>).objectives as unknown[])
                          .length
                      : 0,
                    Number((built.payload.server as Record<string, unknown>)?.forces ?? 0),
                    Number((built.payload.server as Record<string, unknown>)?.slots ?? 0),
                  )}
                </p>
              )}
              {!lastHealth && <p className="muted small">{ca.healthNone}</p>}
            </details>

            {!ended && (
              <details className="card upcoming">
                <summary>
                  {t.upcoming} <span className="chip">{upcoming.length}</span>
                </summary>
                <p className="muted small">{t.upcomingHint}</p>
                <ul className="plain">
                  {upcoming.map((u) => (
                    <li key={u.key} className="row" data-planned={u.key}>
                      <span className="grow">{u.label}</span>
                      <PlannedChip id={u.plannedId} />
                      {plannedEntry(u.plannedId).why && (
                        <span className="chip">
                          {plannedEntry(u.plannedId).why === 'ingame'
                            ? ca.planned.waitsGame
                            : ca.planned.waitsPanel}
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              </details>
            )}
          </div>
        </div>

        {/* Zone 3: the action panel. */}
        <aside className="dir-panel" aria-label={ended ? t.outcomePanel : t.actionPanel}>
          <header className="dir-panel-head">
            <h2>
              <i className={`bi ${ended ? 'bi-flag' : 'bi-sliders'}`} aria-hidden="true" />{' '}
              {ended ? t.outcomePanel : t.actionPanel}
            </h2>
            <p className="muted small">{ended ? t.outcomePanelHint : t.actionPanelHint}</p>
          </header>
          {!ended && failures.length > 0 && (
            <section className="card" aria-label={t.failedTitle}>
              <h2>{t.failedTitle}</h2>
              <p className="muted small">{t.failedHint}</p>
              <ul className="plain">
                {failures.map((o) => (
                  <li key={o.id}>{o.title ?? o.id}</li>
                ))}
              </ul>
              <div className="row actions">
                <button type="button" onClick={() => setEndOpen(true)} disabled={!live}>
                  {ca.endMission}
                </button>
                <button
                  type="button"
                  disabled={!live}
                  onClick={() =>
                    void command('addObjective', {
                      id: `recover_${failures[0].id}`.slice(0, 31),
                      type: 'retrieve_intel',
                      title: t.recoverTitle(failures[0].title ?? failures[0].id),
                      position: failures[0].position,
                      radius: 200,
                      priority: 'secondary',
                    })
                  }
                >
                  {t.recover}
                </button>
              </div>
            </section>
          )}
          <ObjectivesCard
            objectives={def.objectives}
            states={objectiveStates}
            titleOf={(o) => o.title ?? o.id}
            selected={selected}
            onSelect={(id) => setSelected((s) => (s === id ? null : id))}
            canEdit={ended ? undefined : editor.canEdit}
            onEdit={ended ? undefined : (id, patch) => void editor.save(id, patch)}
            onReveal={live ? (o) => void command('revealObjective', { id: o.id }) : undefined}
            onCancel={live ? (o) => void cancelObjective(o) : undefined}
          >
            {!ended && (
              <details className="card newvote">
                <summary>{t.addObjective}</summary>
                <p className="muted small">{t.addObjectiveHint}</p>
                <form
                  className="form"
                  aria-label={t.addObjective}
                  onSubmit={(e) => {
                    e.preventDefault();
                    if (!newPos) return;
                    void command('addObjective', {
                      type: newType,
                      title: newTitle.trim() || undefined,
                      position: newPos,
                      radius: Number(newRadius) || 250,
                      priority: newPriority,
                    }).then(() => {
                      setNewTitle('');
                      setNewPos(null);
                    });
                  }}
                >
                  <label>
                    {t.objectiveType}
                    <select value={newType} onChange={(e) => setNewType(e.target.value)}>
                      {OBJECTIVE_TYPES.map((k) => (
                        <option key={k} value={k}>
                          {ca.objectiveUi.types[k] ?? k}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label>
                    {t.objectiveTitle}
                    <input value={newTitle} onChange={(e) => setNewTitle(e.target.value)} />
                  </label>
                  <div className="two">
                    <label>
                      {t.objectivePriority}
                      <select
                        value={newPriority}
                        onChange={(e) =>
                          setNewPriority(e.target.value as 'main' | 'secondary' | 'hidden')
                        }
                      >
                        {(['main', 'secondary', 'hidden'] as const).map((p) => (
                          <option key={p} value={p}>
                            {ca.priorityNames[p]}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label>
                      {t.objectiveRadius}
                      <input
                        type="number"
                        min={50}
                        value={newRadius}
                        onChange={(e) => setNewRadius(e.target.value)}
                      />
                    </label>
                  </div>
                  <p className="muted small num">
                    {newPos ? t.objectiveAt(newPos[0], newPos[1]) : t.objectivePositionHint}
                  </p>
                  <button type="submit" className="primary" disabled={!newPos}>
                    {t.addObjectiveSend}
                  </button>
                </form>
              </details>
            )}
          </ObjectivesCard>

          {!ended && (
            <section className="dir-group" aria-label={t.nowGroup}>
              <h2 className="group">{t.nowGroup}</h2>
              {commanderCard}
              <section className="card" aria-label={ca.votes}>
                <h2>{ca.votes}</h2>
                <VotesPanel
                  code={code}
                  votes={votes}
                  canDirect
                  onVotes={setVotes}
                  onError={setError}
                />
              </section>
              <article className="card" aria-label={ca.supportsAdd}>
                <h2>{ca.supportsAdd}</h2>
                <p className="muted small">{ca.supportsAddHint}</p>
                <ul className="plain">
                  {SUPPORTS.filter((s) => (def.supports?.[s] ?? 0) > 0).map((s) => (
                    <li key={s} className="row">
                      <span className="grow">{ca.supportNames[s] ?? s}</span>
                      <button
                        type="button"
                        disabled={!live}
                        onClick={() => void command('addSupport', { support: s })}
                        aria-label={`${ca.supportReadyNow} ${ca.supportNames[s] ?? s}`}
                      >
                        {ca.supportReadyNow}
                      </button>
                    </li>
                  ))}
                </ul>
              </article>
              <article className="card" aria-label={t.sendFromCommand}>
                <h2>{t.sendFromCommand}</h2>
                <form
                  className="form"
                  onSubmit={(e) => {
                    e.preventDefault();
                    void command('radio', { text: radioText, from: radioFrom }).then(() =>
                      setRadioText(''),
                    );
                  }}
                >
                  <label>
                    {ca.radioFrom}
                    <input value={radioFrom} onChange={(e) => setRadioFrom(e.target.value)} />
                  </label>
                  <label>
                    {ca.radioText}
                    <input
                      value={radioText}
                      onChange={(e) => setRadioText(e.target.value)}
                      required
                    />
                  </label>
                  <div className="row">
                    <button type="submit" disabled={!live}>
                      {ca.radioSend}
                    </button>
                  </div>
                </form>
                <div className="row">
                  <button
                    type="button"
                    disabled={!live}
                    onClick={() => void command('materialise', { on: true })}
                  >
                    {t.materialise}
                  </button>
                  <button
                    type="button"
                    disabled={!live}
                    onClick={() => void command('materialise', { on: false })}
                  >
                    {t.materialiseOff}
                  </button>
                  <span className="muted small">{t.materialiseHint}</span>
                </div>
                <form
                  className="row"
                  onSubmit={(e) => {
                    e.preventDefault();
                    void command(
                      'sendReinforcements',
                      reinforceTo ? { objectiveId: reinforceTo } : {},
                    );
                  }}
                >
                  <label>
                    {ca.reinforcementsTo}
                    <select value={reinforceTo} onChange={(e) => setReinforceTo(e.target.value)}>
                      <option value="">—</option>
                      {def.objectives.map((o) => (
                        <option key={o.id} value={o.id}>
                          {o.title ?? o.id}
                        </option>
                      ))}
                    </select>
                  </label>
                  <button type="submit" disabled={!live}>
                    {ca.reinforcementsSend}
                  </button>
                </form>
                <form
                  className="form"
                  onSubmit={(e) => {
                    e.preventDefault();
                    const payload: Record<string, unknown> = { event: eventKind };
                    if (eventKind === 'civilian_unrest') payload.objectiveId = eventObjective;
                    if (eventKind === 'weather') payload.weather = weather;
                    void command('triggerEvent', payload);
                  }}
                >
                  <label>
                    {ca.scriptedEvents}
                    <select value={eventKind} onChange={(e) => setEventKind(e.target.value)}>
                      {Object.entries(ca.eventNames).map(([k, v]) => (
                        <option key={k} value={k}>
                          {v}
                        </option>
                      ))}
                    </select>
                  </label>
                  {eventKind === 'civilian_unrest' && (
                    <label>
                      {ca.reinforcementsTo}
                      <select
                        value={eventObjective}
                        onChange={(e) => setEventObjective(e.target.value)}
                        required
                      >
                        <option value="">—</option>
                        {def.objectives.map((o) => (
                          <option key={o.id} value={o.id}>
                            {o.title ?? o.id}
                          </option>
                        ))}
                      </select>
                    </label>
                  )}
                  {eventKind === 'weather' && (
                    <label>
                      {ca.weather}
                      <select value={weather} onChange={(e) => setWeather(e.target.value)}>
                        {Object.entries(ca.weatherNames).map(([k, v]) => (
                          <option key={k} value={k}>
                            {v}
                          </option>
                        ))}
                      </select>
                    </label>
                  )}
                  <div className="row">
                    <button type="submit" disabled={!live}>
                      {ca.fire}
                    </button>
                    <button
                      type="button"
                      disabled={!live}
                      onClick={() => void command('snapshot', {})}
                    >
                      {ca.snapshot}
                    </button>
                  </div>
                </form>
              </article>
              <EnemyReinforcementCard
                live={live}
                objectives={def.objectives}
                point={newPos}
                command={command}
              />
              <EnvironmentCard live={live} command={command} />
              {(def.twists ?? []).length > 0 && (
                <article className="card" aria-label={t.twists}>
                  <h2>{t.twists}</h2>
                  <p className="muted small">{t.twistsHint}</p>
                  <ul className="plain">
                    {(def.twists ?? []).map((tw) => (
                      <li key={tw.id} className="row">
                        <span className="chip">{ca.design.twistKinds[tw.kind] ?? tw.kind}</span>
                        <span>{tw.title}</span>
                        <span className="grow" />
                        <span className="muted small">
                          {tw.byHand || (tw.at ?? 'start') === 'never'
                            ? t.twistByHand
                            : t.twistOnItsOwn}
                        </span>
                        <button
                          type="button"
                          className="small"
                          disabled={!live}
                          onClick={() => void command('twist', { id: tw.id })}
                        >
                          {ca.fire}
                        </button>
                      </li>
                    ))}
                  </ul>
                </article>
              )}
              <section className="card" aria-label={ca.serverChat}>
                <h2>{ca.serverChat}</h2>
                <form className="form" onSubmit={say}>
                  <input
                    value={chat}
                    onChange={(e) => setChat(e.target.value)}
                    aria-label={ca.serverChatText}
                    required
                  />
                  <div className="row">
                    <button type="submit" disabled={data.session.panelServerId === null}>
                      {ca.serverSay}
                    </button>
                    <button
                      type="button"
                      disabled={data.session.panelServerId === null}
                      onClick={() =>
                        void rconSwitch(code)
                          .then(() => setNotice(ca.commandSent))
                          .catch((e) => setError(errorText(e)))
                      }
                    >
                      {ca.serverSwitch}
                    </button>
                  </div>
                </form>
              </section>
              <section className="card" aria-label={ca.commandQueue}>
                <h2>{ca.commandQueue}</h2>
                <ul className="activity">
                  {commands
                    .filter((c) => !c.ackedAt)
                    .slice(-8)
                    .reverse()
                    .map((c) => (
                      <li key={c.id}>
                        <code>{c.kind}</code>{' '}
                        {typeof c.payload.text === 'string' ? c.payload.text : ''}{' '}
                        {c.withdrawnAt ? (
                          <span className="muted small">{ca.withdrawn}</span>
                        ) : c.deliveredAt ? (
                          <span className="muted small">{ca.delivered}</span>
                        ) : (
                          <button
                            type="button"
                            onClick={() =>
                              void withdrawCommand(code, c.id)
                                .then((w) =>
                                  setCommands((list) => list.map((x) => (x.id === w.id ? w : x))),
                                )
                                .catch((e) => setError(errorText(e)))
                            }
                          >
                            {ca.undo}
                          </button>
                        )}
                      </li>
                    ))}
                  {acks.map((e) => (
                    <li key={e.seq}>
                      <span className="muted small num">{Math.floor(e.at / 60)}′</span>{' '}
                      {describeEvent(e)}
                    </li>
                  ))}
                </ul>
              </section>
            </section>
          )}

          {/* How to get in is for a night that is still to be played or being played. */}
          {!ended && <JoinCard server={data.server ?? null} />}
        </aside>
      </div>
    </Shell>
  );
}

/** "actualitzat fa N s", ticking on its own so the page around it does not redraw every second. */
function Updated({ since }: { since: number | null }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);
  if (since === null) return null;
  return (
    <span className="muted small num">
      {ca.live.updated(Math.max(0, Math.floor((now - since) / 1000)))}
    </span>
  );
}

/**
 * The ended night's two map layers, as switches with their name and colour above the map
 * (the owner, web card 222, 2026-10-19: «Treu la marca de "Moviment"» — "no veig com fer-ho";
 * they had been two small checkboxes in a line of text).
 */
function MapLayers({
  hasReport,
  movement,
  deaths,
  onMovement,
  onDeaths,
}: {
  hasReport: boolean;
  movement: boolean;
  deaths: boolean;
  onMovement: (on: boolean) => void;
  onDeaths: (on: boolean) => void;
}) {
  const t = ca.directionPage;
  if (!hasReport) {
    return (
      <div className="dir-map-layers">
        <p className="muted small">{t.mapNoReport}</p>
      </div>
    );
  }
  const layers = [
    { key: 'movement', label: t.mapMovement, on: movement, set: onMovement },
    { key: 'deaths', label: t.mapDeaths, on: deaths, set: onDeaths },
  ];
  return (
    <div className="dir-map-layers" role="group" aria-label={t.mapLayers}>
      <strong className="layers-title">
        <i className="bi bi-layers" aria-hidden="true" /> {t.mapLayers}
      </strong>
      <div className="row">
        {layers.map((l) => (
          <button
            key={l.key}
            type="button"
            className={`layer-toggle${l.on ? ' on' : ''}`}
            aria-pressed={l.on}
            title={l.on ? t.mapLayerHide(l.label) : t.mapLayerShow(l.label)}
            onClick={() => l.set(!l.on)}
          >
            <i className={`bi ${l.on ? 'bi-eye' : 'bi-eye-slash'}`} aria-hidden="true" />
            <span className={`swatch ${l.key}`} aria-hidden="true" />
            {l.label}
            <span className="muted small">{l.on ? t.mapLayerOn : t.mapLayerOff}</span>
          </button>
        ))}
      </div>
      <p className="muted small">{t.mapLayersHint}</p>
    </div>
  );
}
